// src/pages/customer-panel/settings/SettingsPage.js
// Settings Hub Page - Multi-user Chatbot Settings + Central Drive (Admin only)

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Switch,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormControlLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Alert,
    Snackbar,
    IconButton,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Tooltip,
    Breadcrumbs,
    Link,
    Checkbox,
    Divider,
    Drawer,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloudIcon from '@mui/icons-material/Cloud';
import SaveIcon from '@mui/icons-material/Save';
import LockIcon from '@mui/icons-material/Lock';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import FolderIcon from '@mui/icons-material/Folder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import WarningIcon from '@mui/icons-material/Warning';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import RefreshIcon from '@mui/icons-material/Refresh';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PaletteIcon from '@mui/icons-material/Palette';
import PersonIcon from '@mui/icons-material/Person';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import axiosInstance from '../../../utils/axiosInstance';
import CreatePrompts from '../../../components/CreatePrompts';
import { LanguageSelection, VoiceSelection } from './languageData';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

const accordionSx = {
    borderRadius: '12px !important',
    border: '1px solid',
    borderColor: 'grey.100',
    boxShadow: 'none',
    '&:before': { display: 'none' },
    mb: 2,
    '&.Mui-expanded': { mb: 2 },
};

const inputSx = {
    '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' },
    '& .MuiInputLabel-root': { fontFamily },
    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': { borderColor: GREEN_ACCENT },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: GREEN_ACCENT },
};

const switchSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: GREEN_ACCENT },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: GREEN_ACCENT },
};

const btnSx = {
    fontFamily,
    fontWeight: 600,
    bgcolor: GREEN_ACCENT,
    borderRadius: '10px',
    textTransform: 'none',
    '&:hover': { bgcolor: '#059669' },
};

// =================================================================
// FOLDER SELECTION MODAL (for Central Drive)
// =================================================================
function FolderSelectionModal({ open, onClose, driveType, onFolderSelected }) {
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [breadcrumbs, setBreadcrumbs] = useState([{ id: 'root', name: 'My Drive' }]);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [showNewFolder, setShowNewFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [creatingFolder, setCreatingFolder] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [createSubfolder, setCreateSubfolder] = useState(true);
    const [subfolderName, setSubfolderName] = useState('App_User_Documents');

    const currentParentId = breadcrumbs[breadcrumbs.length - 1].id;
    const apiPrefix = driveType === 'google_drive' ? '/api/google-drive' : '/api/onedrive';

    const fetchFolders = useCallback(async (parentId) => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`${apiPrefix}/folders`, { params: { parent_id: parentId } });
            if (res.data.success) setFolders(res.data.folders || []);
        } catch { /* handled */ } finally { setLoading(false); }
    }, [apiPrefix]);

    useEffect(() => {
        if (open) fetchFolders(currentParentId);
    }, [open, currentParentId, fetchFolders]);

    const handleFolderClick = (folder) => setSelectedFolder(folder);

    const handleFolderDoubleClick = (folder) => {
        setBreadcrumbs(prev => [...prev, { id: folder.id, name: folder.name }]);
        setSelectedFolder(null);
    };

    const handleBreadcrumbClick = (index) => {
        setBreadcrumbs(prev => prev.slice(0, index + 1));
        setSelectedFolder(null);
    };

    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) return;
        setCreatingFolder(true);
        try {
            const res = await axiosInstance.post(`${apiPrefix}/folders`, {
                folder_name: newFolderName.trim(),
                parent_id: currentParentId,
            });
            if (res.data.success) {
                setNewFolderName('');
                setShowNewFolder(false);
                fetchFolders(currentParentId);
            }
        } catch { /* handled */ } finally { setCreatingFolder(false); }
    };

    const handleConfirm = async () => {
        const folder = selectedFolder || { id: currentParentId, name: breadcrumbs[breadcrumbs.length - 1].name };
        setConfirming(true);
        try {
            const res = await axiosInstance.post(`${apiPrefix}/set-folder`, {
                folder_id: folder.id,
                folder_name: folder.name,
                create_subfolder: createSubfolder,
                subfolder_name: subfolderName,
            });
            if (res.data.success) {
                onFolderSelected(res.data.folder.id, res.data.folder.name);
                onClose();
            }
        } catch { /* handled */ } finally { setConfirming(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FolderIcon sx={{ color: GREEN_ACCENT }} />
                    Select Folder
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            {/* Breadcrumbs */}
            <Box sx={{ px: 3, py: 1, bgcolor: '#f8fafc', borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'grey.100' }}>
                <Breadcrumbs separator={<NavigateNextIcon sx={{ fontSize: 16 }} />}>
                    {breadcrumbs.map((crumb, i) => (
                        <Link
                            key={crumb.id}
                            component="button"
                            underline="hover"
                            onClick={() => handleBreadcrumbClick(i)}
                            sx={{
                                fontFamily,
                                fontSize: '0.8rem',
                                fontWeight: i === breadcrumbs.length - 1 ? 700 : 500,
                                color: i === breadcrumbs.length - 1 ? GREEN_ACCENT : '#64748b',
                            }}
                        >
                            {crumb.name}
                        </Link>
                    ))}
                </Breadcrumbs>
            </Box>

            {/* Toolbar */}
            <Box sx={{ px: 3, py: 1.5, display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'grey.100' }}>
                <Button size="small" startIcon={<RefreshIcon sx={{ fontSize: 16 }} />} onClick={() => fetchFolders(currentParentId)} disabled={loading}
                    sx={{ fontFamily, textTransform: 'none', color: '#64748b', fontSize: '0.8rem' }}>
                    Refresh
                </Button>
                <Button size="small" startIcon={<CreateNewFolderIcon sx={{ fontSize: 16 }} />} onClick={() => setShowNewFolder(true)}
                    sx={{ fontFamily, textTransform: 'none', color: GREEN_ACCENT, fontSize: '0.8rem' }}>
                    New Folder
                </Button>
            </Box>

            {/* New Folder Input */}
            {showNewFolder && (
                <Box sx={{ px: 3, py: 1.5, bgcolor: '#f0fdf4', display: 'flex', gap: 1, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Folder name"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                        autoFocus
                        sx={{ ...inputSx, '& .MuiInputBase-root': { ...inputSx['& .MuiInputBase-root'], fontSize: '0.85rem' } }}
                    />
                    <Button variant="contained" size="small" onClick={handleCreateFolder} disabled={creatingFolder}
                        sx={{ ...btnSx, minWidth: 80, fontSize: '0.8rem' }}>
                        {creatingFolder ? <CircularProgress size={16} color="inherit" /> : 'Create'}
                    </Button>
                    <Button size="small" onClick={() => { setShowNewFolder(false); setNewFolderName(''); }}
                        sx={{ fontFamily, textTransform: 'none', color: '#64748b', fontSize: '0.8rem' }}>Cancel</Button>
                </Box>
            )}

            <DialogContent sx={{ minHeight: 200, maxHeight: 300, p: 2 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 4 }}>
                        <CircularProgress size={32} sx={{ color: GREEN_ACCENT }} />
                    </Box>
                ) : folders.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4, color: '#94a3b8' }}>
                        <FolderIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
                        <Typography sx={{ fontFamily, fontSize: '0.85rem' }}>No folders found</Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.75rem', mt: 0.5 }}>Create a new folder or use this location</Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {folders.map(folder => (
                            <Box
                                key={folder.id}
                                onClick={() => handleFolderClick(folder)}
                                onDoubleClick={() => handleFolderDoubleClick(folder)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 1.5,
                                    borderRadius: '10px',
                                    border: '1px solid',
                                    borderColor: selectedFolder?.id === folder.id ? GREEN_ACCENT : 'grey.100',
                                    bgcolor: selectedFolder?.id === folder.id ? `${GREEN_ACCENT}08` : 'transparent',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                    '&:hover': { bgcolor: '#f8fafc' },
                                }}
                            >
                                <FolderIcon sx={{ color: selectedFolder?.id === folder.id ? GREEN_ACCENT : '#f59e0b', fontSize: 20 }} />
                                <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 500, flex: 1 }}>{folder.name}</Typography>
                                {selectedFolder?.id === folder.id && <CheckCircleIcon sx={{ fontSize: 18, color: GREEN_ACCENT }} />}
                                <NavigateNextIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                            </Box>
                        ))}
                    </Box>
                )}
            </DialogContent>

            {/* Subfolder Option */}
            <Box sx={{ px: 3, py: 2, bgcolor: '#f8fafc', borderTop: '1px solid', borderColor: 'grey.100' }}>
                <FormControlLabel
                    control={<Checkbox checked={createSubfolder} onChange={(e) => setCreateSubfolder(e.target.checked)} sx={{ color: GREEN_ACCENT, '&.Mui-checked': { color: GREEN_ACCENT } }} />}
                    label={<Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 500 }}>Create a subfolder for app documents</Typography>}
                />
                {createSubfolder && (
                    <TextField
                        size="small"
                        fullWidth
                        value={subfolderName}
                        onChange={(e) => setSubfolderName(e.target.value)}
                        placeholder="Subfolder name"
                        sx={{ mt: 1, ...inputSx }}
                    />
                )}
            </Box>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
                <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', flex: 1 }}>
                    {selectedFolder ? `Selected: ${selectedFolder.name}` : `Using: ${breadcrumbs[breadcrumbs.length - 1].name}`}
                </Typography>
                <Button onClick={onClose} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                <Button variant="contained" onClick={handleConfirm} disabled={confirming}
                    startIcon={confirming ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                    sx={btnSx}>
                    {confirming ? 'Configuring...' : 'Use This Folder'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// =================================================================
// MAIN SETTINGS PAGE
// =================================================================
export default function SettingsPage() {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const userRole = user?.active_team?.user_role_in_team || 'customer';
    const isAdmin = userRole === 'admin';
    const currentUserId = user?.user_id;

    const [expanded, setExpanded] = useState('chatbot');
    const [loading, setLoading] = useState({});
    const [saving, setSaving] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // ---- MULTI-USER STATE (Admin feature) ----
    const [viewFilter, setViewFilter] = useState('mine'); // 'mine' or 'all_users'
    const [teamMembers, setTeamMembers] = useState([]);
    const [selectedFilterUser, setSelectedFilterUser] = useState(null);
    const [targetUserId, setTargetUserId] = useState(currentUserId);

    // ---- CHATBOT SETTINGS STATE ----
    const [chatbotSettings, setChatbotSettings] = useState({
        is_chatbot_available: true,
        current_chatbot_name: '',
        current_website_address: '',
        old_chatbot_name: '',
        old_website_address: '',
        primary_color: '#000000',
        secondary_color: '#ffffff',
        greeting_text: '',
        prompt_text: '',
        is_old_alias_available: false,
        old_chatbot_identity: '',
        is_current_alias_available: true,
        is_creator_live_agent_available: false,
        live_agent_chatbot_identity: '',
    });
    const [chatbots, setChatbots] = useState([]);
    const [chatbotsLoading, setChatbotsLoading] = useState(false);
    const primaryColorRef = useRef(null);
    const secondaryColorRef = useRef(null);
    const createPromptsRef = useRef(null);
    const [isPromptDrawerOpen, setIsPromptDrawerOpen] = useState(false);
    const [promptFormValid, setPromptFormValid] = useState(false);
    const [promptGenerating, setPromptGenerating] = useState(false);

    // ---- LANGUAGE SETTINGS STATE ----
    const [languages, setLanguages] = useState([]);
    const [selectedLanguage, setSelectedLanguage] = useState('');
    const [selectedGender, setSelectedGender] = useState('');
    const [selectedVoice, setSelectedVoice] = useState('');
    const [isDefaultLang, setIsDefaultLang] = useState(false);
    const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
    const [audioLoading, setAudioLoading] = useState(null);
    const audioPlayerRef = useRef(null);

    // Filter voices based on selected language and gender
    const availableVoices = useMemo(() => {
        if (!selectedLanguage || !selectedGender) return [];
        return VoiceSelection.filter(v => v.language === selectedLanguage && v.gender === selectedGender);
    }, [selectedLanguage, selectedGender]);

    // Reset voice when language/gender changes
    useEffect(() => {
        setSelectedVoice('');
    }, [selectedLanguage, selectedGender]);

    // Cleanup audio on unmount
    useEffect(() => {
        return () => {
            if (audioPlayerRef.current) {
                audioPlayerRef.current.pause();
                audioPlayerRef.current = null;
            }
        };
    }, []);

    // ---- CENTRAL DRIVE STATE ----
    const [selectedDrive, setSelectedDrive] = useState(null);
    const [googleDetails, setGoogleDetails] = useState(null);
    const [oneDriveDetails, setOneDriveDetails] = useState(null);
    const [googleFolderConfigured, setGoogleFolderConfigured] = useState(false);
    const [oneDriveFolderConfigured, setOneDriveFolderConfigured] = useState(false);
    const [driveActionLoading, setDriveActionLoading] = useState({});
    const [folderModalDrive, setFolderModalDrive] = useState(null);
    const [driveConfirmModal, setDriveConfirmModal] = useState({ type: null, drive: null });

    const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    // =========================================================
    // FETCH FUNCTIONS
    // =========================================================
    const fetchChatbotSettings = useCallback(async (userId) => {
        if (!userId) return;
        setLoading(prev => ({ ...prev, chatbot: true }));
        try {
            const res = await axiosInstance.get(`/settings/chatbot-main-settings/${userId}`);
            const data = res.data?.data || res.data;
            if (data) setChatbotSettings(prev => ({ ...prev, ...data }));
        } catch (err) {
            console.error('Failed to fetch chatbot settings:', err);
            showSnackbar(err.response?.data?.message || 'Failed to load chatbot settings.', 'error');
        } finally {
            setLoading(prev => ({ ...prev, chatbot: false }));
        }
    }, []);

    const fetchChatbots = useCallback(async () => {
        setChatbotsLoading(true);
        try {
            const res = await axiosInstance.get('/settings/get-chatbots');
            setChatbots(res.data?.chatbots || res.data?.data || []);
        } catch { /* optional */ } finally { setChatbotsLoading(false); }
    }, []);

    const fetchLanguages = useCallback(async (userId) => {
        if (!userId) return;
        try {
            const res = await axiosInstance.get(`/settings/chatbot-language-settings/${userId}`);
            setLanguages(res.data?.data || []);
        } catch {
            setLanguages([]);
        }
    }, []);

    const fetchTeamMembers = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/users/team-members');
            setTeamMembers(res.data?.data || res.data?.members || []);
        } catch { /* optional */ }
    }, []);

    const fetchDriveSettings = useCallback(async () => {
        setLoading(prev => ({ ...prev, drive: true }));
        try {
            const [settingsRes, googleRes, onedriveRes] = await Promise.allSettled([
                axiosInstance.get('/settings/drive'),
                axiosInstance.get('/api/google-drive/customer-status'),
                axiosInstance.get('/api/onedrive/customer-status'),
            ]);
            if (settingsRes.status === 'fulfilled') {
                const sd = settingsRes.value.data;
                setSelectedDrive(sd.selected_drive || null);
                if (sd.google_drive_configured) setGoogleFolderConfigured(true);
                if (sd.onedrive_configured) setOneDriveFolderConfigured(true);
            }
            if (googleRes.status === 'fulfilled' && googleRes.value.data.is_connected) {
                setGoogleDetails(googleRes.value.data.details);
                setGoogleFolderConfigured(googleRes.value.data.is_folder_configured);
            }
            if (onedriveRes.status === 'fulfilled' && onedriveRes.value.data.is_connected) {
                setOneDriveDetails(onedriveRes.value.data.details);
                setOneDriveFolderConfigured(onedriveRes.value.data.is_folder_configured);
            }
        } catch {
            console.error('Failed to fetch drive settings');
        } finally {
            setLoading(prev => ({ ...prev, drive: false }));
        }
    }, []);

    // Initial data fetch
    useEffect(() => {
        document.title = 'Settings | Customer Panel';
        if (currentUserId) {
            fetchChatbotSettings(currentUserId);
            fetchChatbots();
            fetchLanguages(currentUserId);
        }
        if (isAdmin) {
            fetchTeamMembers();
            fetchDriveSettings();
        }
    }, [currentUserId, isAdmin, fetchChatbotSettings, fetchChatbots, fetchLanguages, fetchTeamMembers, fetchDriveSettings]);

    // Re-fetch when target user changes
    useEffect(() => {
        if (targetUserId) {
            fetchChatbotSettings(targetUserId);
            fetchLanguages(targetUserId);
        }
    }, [targetUserId, fetchChatbotSettings, fetchLanguages]);

    // =========================================================
    // MULTI-USER HANDLERS (Admin)
    // =========================================================
    const handleViewFilterChange = (newView) => {
        setViewFilter(newView);
        if (newView === 'mine') {
            setSelectedFilterUser(null);
            setTargetUserId(currentUserId);
        } else {
            setSelectedFilterUser(null);
            setTargetUserId(null);
        }
    };

    const handleSelectTeamMember = (memberId) => {
        if (!memberId) {
            setSelectedFilterUser(null);
            setTargetUserId(null);
            return;
        }
        const member = teamMembers.find(m => m.id === memberId);
        setSelectedFilterUser(member || null);
        setTargetUserId(memberId);
    };

    const getFormTitle = () => {
        if (!isAdmin || viewFilter === 'mine') return 'My Chatbot Settings';
        if (selectedFilterUser) {
            const name = selectedFilterUser.name || selectedFilterUser.email || `User ${selectedFilterUser.id}`;
            const role = selectedFilterUser.role || '';
            return `Editing Settings for: ${name}${role ? ` (${role.charAt(0).toUpperCase() + role.slice(1)})` : ''}`;
        }
        return 'Chatbot Settings';
    };

    // =========================================================
    // SAVE HANDLERS
    // =========================================================
    const handleSaveChatbot = async () => {
        if (!targetUserId) {
            showSnackbar('Please select a user first.', 'error');
            return;
        }

        if (chatbotSettings.is_old_alias_available && !chatbotSettings.old_chatbot_identity) {
            showSnackbar('Please select a knowledge base when Public Knowledge Base is enabled.', 'error');
            return;
        }
        if (chatbotSettings.is_creator_live_agent_available && !chatbotSettings.live_agent_chatbot_identity) {
            showSnackbar('Please select a chatbot for Live Agent.', 'error');
            return;
        }

        setSaving(prev => ({ ...prev, chatbot: true }));
        try {
            await axiosInstance.put(`/settings/chatbot-main-settings/${targetUserId}`, chatbotSettings);
            showSnackbar('Chatbot settings saved successfully!');
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Failed to save chatbot settings.', 'error');
        } finally {
            setSaving(prev => ({ ...prev, chatbot: false }));
        }
    };

    // Get the chatbot numeric ID - from selected knowledge base, or fallback to default/first chatbot
    const getSelectedChatbotId = () => {
        const identity = chatbotSettings.old_chatbot_identity;
        if (identity) {
            const cb = chatbots.find(c => c.chatbot_identity === identity);
            if (cb) return cb.id;
        }
        // Fallback: default chatbot or first chatbot
        const defaultCb = chatbots.find(c => c.is_default) || chatbots[0];
        return defaultCb?.id || null;
    };

    const handleGeneratePrompt = async () => {
        if (!createPromptsRef.current?.handleGeneratePrompt) return;
        setPromptGenerating(true);
        try {
            const result = await createPromptsRef.current.handleGeneratePrompt();
            if (result === true) {
                showSnackbar('Prompt generated and applied!');
                setIsPromptDrawerOpen(false);
                // Re-fetch to get the updated prompt_text and greeting_text
                if (targetUserId) fetchChatbotSettings(targetUserId);
            }
        } catch {
            showSnackbar('Failed to generate prompt.', 'error');
        } finally {
            setPromptGenerating(false);
        }
    };

    const handleAddLanguage = async () => {
        if (!selectedLanguage || !selectedGender || !selectedVoice) {
            showSnackbar('Please select language, gender, and voice.', 'error');
            return;
        }
        if (!targetUserId) {
            showSnackbar('Please select a user first.', 'error');
            return;
        }
        try {
            const lang = LanguageSelection.find(l => l.value === selectedLanguage);
            await axiosInstance.post('/settings/chatbot-language-settings', {
                user_id: targetUserId,
                website_lang: lang?.label || selectedLanguage,
                website_lang_code: selectedLanguage,
                website_gender: selectedGender,
                website_voice: selectedVoice,
                default_language: isDefaultLang,
            });
            showSnackbar('Language added!');
            setSelectedLanguage('');
            setSelectedGender('');
            setSelectedVoice('');
            setIsDefaultLang(false);
            fetchLanguages(targetUserId);
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Failed to add language.', 'error');
        }
    };

    const handleDeleteLanguage = async (langId) => {
        try {
            await axiosInstance.delete(`/settings/chatbot-language-settings/${langId}`);
            showSnackbar('Language removed.');
            fetchLanguages(targetUserId);
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Failed to delete language.', 'error');
        }
    };

    // ---- AUDIO PREVIEW ----
    const stopCurrentAudio = () => {
        if (audioPlayerRef.current) {
            audioPlayerRef.current.pause();
            audioPlayerRef.current.currentTime = 0;
            audioPlayerRef.current = null;
        }
        setCurrentlyPlaying(null);
    };

    const playAudio = async (voiceValue) => {
        try {
            if (currentlyPlaying === voiceValue) {
                stopCurrentAudio();
                return;
            }
            stopCurrentAudio();
            setAudioLoading(voiceValue);

            const audioSrc = require(`../../../assets/audio/${voiceValue}.wav`);
            const newAudio = new Audio(audioSrc);
            audioPlayerRef.current = newAudio;

            newAudio.onerror = () => {
                setAudioLoading(null);
                setCurrentlyPlaying(null);
                showSnackbar('Audio preview not available for this voice.', 'error');
            };
            newAudio.onloadstart = () => { setCurrentlyPlaying(voiceValue); setAudioLoading(null); };
            newAudio.onended = () => { setCurrentlyPlaying(null); audioPlayerRef.current = null; };
            newAudio.onpause = () => { setCurrentlyPlaying(null); };

            await newAudio.play();
        } catch {
            setAudioLoading(null);
            setCurrentlyPlaying(null);
            showSnackbar('Audio preview not available for this voice.', 'error');
        }
    };

    // ---- DRIVE HANDLERS ----
    const handleConnectDrive = async (driveType) => {
        setDriveActionLoading(prev => ({ ...prev, [driveType]: 'connect' }));
        const url = driveType === 'google_drive' ? '/api/google-drive/auth/url' : '/api/onedrive/auth/url';
        try {
            const res = await axiosInstance.get(url);
            window.location.href = res.data.url;
        } catch {
            showSnackbar('Failed to get authorization URL.', 'error');
            setDriveActionLoading(prev => ({ ...prev, [driveType]: null }));
        }
    };

    const handleFolderSelected = async (driveType, folderId, folderName) => {
        if (driveType === 'google_drive') {
            setGoogleFolderConfigured(true);
            if (googleDetails) setGoogleDetails(prev => ({ ...prev, folder_id: folderId }));
        } else {
            setOneDriveFolderConfigured(true);
            if (oneDriveDetails) setOneDriveDetails(prev => ({ ...prev, folder_id: folderId }));
        }
        if (!selectedDrive) {
            try {
                await axiosInstance.put('/settings/drive', { selected_drive: driveType });
                setSelectedDrive(driveType);
                showSnackbar('Drive configured and set as default!');
            } catch {
                showSnackbar('Folder configured, but failed to set as default.', 'error');
            }
        } else {
            showSnackbar('Folder configured successfully!');
        }
    };

    const handleSetDefaultDrive = async () => {
        const drive = driveConfirmModal.drive;
        if (!drive) return;
        const isFolderOk = drive === 'google_drive' ? googleFolderConfigured : oneDriveFolderConfigured;
        if (!isFolderOk) { showSnackbar('Configure a folder first.', 'error'); setDriveConfirmModal({ type: null, drive: null }); return; }
        setDriveActionLoading(prev => ({ ...prev, [drive]: 'setDefault' }));
        try {
            await axiosInstance.put('/settings/drive', { selected_drive: drive });
            setSelectedDrive(drive);
            showSnackbar('Default drive updated!');
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Failed to set default.', 'error');
        } finally {
            setDriveActionLoading(prev => ({ ...prev, [drive]: null }));
            setDriveConfirmModal({ type: null, drive: null });
        }
    };

    const handleDisconnectDrive = async () => {
        const drive = driveConfirmModal.drive;
        if (!drive) return;
        setDriveActionLoading(prev => ({ ...prev, [drive]: 'disconnect' }));
        const apiPrefix = drive === 'google_drive' ? '/api/google-drive' : '/api/onedrive';
        try {
            await axiosInstance.delete(`${apiPrefix}/disconnect`);
            if (drive === 'google_drive') { setGoogleDetails(null); setGoogleFolderConfigured(false); }
            else { setOneDriveDetails(null); setOneDriveFolderConfigured(false); }
            if (selectedDrive === drive) setSelectedDrive(null);
            showSnackbar(`${drive === 'google_drive' ? 'Google Drive' : 'OneDrive'} disconnected.`);
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Failed to disconnect.', 'error');
        } finally {
            setDriveActionLoading(prev => ({ ...prev, [drive]: null }));
            setDriveConfirmModal({ type: null, drive: null });
        }
    };

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleChatbotChange = (e) => {
        const { name, value } = e.target;
        setChatbotSettings(prev => ({ ...prev, [name]: value }));
    };

    const handleChatbotToggle = (name) => (e) => {
        setChatbotSettings(prev => ({
            ...prev,
            [name]: e.target.checked,
            ...(name === 'is_old_alias_available' && !e.target.checked ? { old_chatbot_identity: '' } : {}),
            ...(name === 'is_creator_live_agent_available' && !e.target.checked ? { live_agent_chatbot_identity: '' } : {}),
        }));
    };

    // ---- DRIVE OPTIONS ----
    const driveOptions = [
        { type: 'google_drive', name: 'Google Drive', details: googleDetails, folderConfigured: googleFolderConfigured },
        { type: 'onedrive', name: 'Microsoft OneDrive', details: oneDriveDetails, folderConfigured: oneDriveFolderConfigured },
    ];

    const isSetupComplete = selectedDrive && (
        (selectedDrive === 'google_drive' && googleFolderConfigured) ||
        (selectedDrive === 'onedrive' && oneDriveFolderConfigured)
    );

    const driveName = (d) => d === 'google_drive' ? 'Google Drive' : 'OneDrive';

    // Helper: get voice display name
    const getVoiceName = (voiceValue) => {
        const voice = VoiceSelection.find(v => v.value === voiceValue);
        return voice ? voice.label : voiceValue;
    };

    // Helper: render play/stop button for a configured language
    const renderPlayButton = (voiceValue) => {
        const isPlaying = currentlyPlaying === voiceValue;
        const isLoading = audioLoading === voiceValue;

        if (isLoading) {
            return (
                <IconButton size="small" disabled sx={{ color: GREEN_ACCENT }}>
                    <CircularProgress size={18} sx={{ color: GREEN_ACCENT }} />
                </IconButton>
            );
        }

        return (
            <IconButton size="small" onClick={() => playAudio(voiceValue)}
                sx={{ color: isPlaying ? '#fff' : GREEN_ACCENT, bgcolor: isPlaying ? GREEN_ACCENT : `${GREEN_ACCENT}15`, '&:hover': { bgcolor: isPlaying ? '#059669' : `${GREEN_ACCENT}25` } }}>
                {isPlaying ? <StopIcon sx={{ fontSize: 18 }} /> : <PlayArrowIcon sx={{ fontSize: 18 }} />}
            </IconButton>
        );
    };

    // ---- CHATBOT SETTINGS CONTENT ----
    const renderChatbotSettings = () => {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Admin User Filter */}
                {isAdmin && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {['mine', 'all_users'].map(v => (
                                <Button
                                    key={v}
                                    variant={viewFilter === v ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => handleViewFilterChange(v)}
                                    sx={{
                                        fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '10px', fontSize: '0.8rem',
                                        ...(viewFilter === v
                                            ? { bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: '#059669' } }
                                            : { borderColor: '#e2e8f0', color: '#64748b' }),
                                    }}
                                >
                                    {v === 'mine' ? 'My Settings' : 'A Specific User'}
                                </Button>
                            ))}
                        </Box>
                        {viewFilter === 'all_users' && (
                            <FormControl size="small" sx={{ minWidth: 250 }}>
                                <InputLabel sx={{ fontFamily }}>Select Team Member</InputLabel>
                                <Select
                                    value={targetUserId || ''}
                                    onChange={(e) => handleSelectTeamMember(e.target.value || null)}
                                    label="Select Team Member"
                                    sx={{ fontFamily, borderRadius: '10px' }}
                                >
                                    <MenuItem value="" sx={{ fontFamily }}>Select a user...</MenuItem>
                                    {teamMembers.map(m => (
                                        <MenuItem key={m.id} value={m.id} sx={{ fontFamily }}>
                                            {m.name || m.email}{m.role ? ` (${m.role})` : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                )}

                {/* Content: placeholder, loading, or form */}
                {isAdmin && viewFilter === 'all_users' && !targetUserId ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <PersonIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>Please select a team member to edit their settings.</Typography>
                    </Box>
                ) : loading.chatbot ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} sx={{ color: GREEN_ACCENT }} /></Box>
                ) : (
                    <>
                {/* Form Title */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: GREEN_ACCENT }}>{getFormTitle()}</Typography>

                {/* General Settings */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>General Settings</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Public Chatbot Name" value={chatbotSettings.old_chatbot_name || 'Not Set'} InputProps={{ readOnly: true }} fullWidth size="small"
                            sx={{ ...inputSx, '& .MuiInputBase-root': { ...inputSx['& .MuiInputBase-root'], bgcolor: '#f8fafc' } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Your Chatbot Name" name="current_chatbot_name" value={chatbotSettings.current_chatbot_name || ''} onChange={handleChatbotChange} fullWidth size="small" sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Public Website" value={chatbotSettings.old_website_address || 'Not Set'} InputProps={{ readOnly: true }} fullWidth size="small"
                            sx={{ ...inputSx, '& .MuiInputBase-root': { ...inputSx['& .MuiInputBase-root'], bgcolor: '#f8fafc' } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Your Website Address" name="current_website_address" value={chatbotSettings.current_website_address || ''} onChange={handleChatbotChange} placeholder="https://example.com" fullWidth size="small" sx={inputSx} />
                    </Grid>
                </Grid>

                <Divider />

                {/* Appearance */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>
                    <PaletteIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom' }} /> Appearance
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 500, mb: 1 }}>Primary Color</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                onClick={() => primaryColorRef.current?.click()}
                                sx={{ width: 40, height: 40, borderRadius: '8px', bgcolor: chatbotSettings.primary_color || '#000000', border: '2px solid', borderColor: 'grey.200', cursor: 'pointer' }}
                            />
                            <input ref={primaryColorRef} type="color" name="primary_color" value={chatbotSettings.primary_color || '#000000'} onChange={handleChatbotChange} style={{ position: 'absolute', visibility: 'hidden' }} />
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, fontFamily: 'monospace' }}>{(chatbotSettings.primary_color || '#000000').toUpperCase()}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 500, mb: 1 }}>Secondary Color</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                onClick={() => secondaryColorRef.current?.click()}
                                sx={{ width: 40, height: 40, borderRadius: '8px', bgcolor: chatbotSettings.secondary_color || '#ffffff', border: '2px solid', borderColor: 'grey.200', cursor: 'pointer' }}
                            />
                            <input ref={secondaryColorRef} type="color" name="secondary_color" value={chatbotSettings.secondary_color || '#ffffff'} onChange={handleChatbotChange} style={{ position: 'absolute', visibility: 'hidden' }} />
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, fontFamily: 'monospace' }}>{(chatbotSettings.secondary_color || '#ffffff').toUpperCase()}</Typography>
                        </Box>
                    </Grid>
                </Grid>

                <Divider />

                {/* Behavior */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>Behavior</Typography>
                    <Button
                        size="small"
                        startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                        onClick={() => setIsPromptDrawerOpen(true)}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', color: GREEN_ACCENT, borderColor: GREEN_ACCENT, borderRadius: '10px' }}
                        variant="outlined"
                    >
                        Generate Prompt
                    </Button>
                </Box>
                <TextField label="Greeting Message" name="greeting_text" value={chatbotSettings.greeting_text || ''} onChange={handleChatbotChange}
                    fullWidth size="small" multiline rows={3} placeholder="Enter the first message the chatbot shows..." sx={inputSx} />
                <TextField label="System Prompt" name="prompt_text" value={chatbotSettings.prompt_text || ''} onChange={handleChatbotChange}
                    fullWidth size="small" multiline rows={5} placeholder="Define the chatbot's personality, role, and instructions..." sx={inputSx} />

                <Divider />

                {/* Knowledge Sources */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>Knowledge Sources</Typography>
                <Box sx={{ p: 2, borderRadius: '12px', border: '1px solid', borderColor: 'grey.100', bgcolor: '#fafbfc' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SmartToyIcon sx={{ fontSize: 18, color: '#8b5cf6' }} />
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem' }}>Public Knowledge Base</Typography>
                        </Box>
                        <Switch
                            checked={!!chatbotSettings.is_old_alias_available}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                setChatbotSettings(prev => ({
                                    ...prev,
                                    is_old_alias_available: checked,
                                    ...(!checked ? { old_chatbot_identity: '' } : {}),
                                }));
                            }}
                            sx={switchSx}
                            size="small"
                        />
                    </Box>
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', mb: 1.5 }}>Use a chatbot as the public knowledge base.</Typography>
                    {chatbotSettings.is_old_alias_available && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {chatbots.length === 0 ? (
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8', textAlign: 'center', py: 2 }}>No chatbots available</Typography>
                            ) : chatbots.map(cb => {
                                const isSelected = chatbotSettings.old_chatbot_identity === cb.chatbot_identity;
                                return (
                                    <Box
                                        key={cb.chatbot_identity}
                                        onClick={() => setChatbotSettings(prev => ({
                                            ...prev,
                                            old_chatbot_identity: isSelected ? '' : cb.chatbot_identity,
                                        }))}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
                                            borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s ease',
                                            border: '2px solid', bgcolor: isSelected ? `${GREEN_ACCENT}08` : '#fff',
                                            borderColor: isSelected ? GREEN_ACCENT : 'grey.200',
                                            '&:hover': { borderColor: isSelected ? GREEN_ACCENT : '#cbd5e1', bgcolor: isSelected ? `${GREEN_ACCENT}08` : '#f8fafc' },
                                        }}
                                    >
                                        <Box sx={{
                                            width: 36, height: 36, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            bgcolor: isSelected ? `${GREEN_ACCENT}15` : '#f1f5f9',
                                        }}>
                                            <SmartToyIcon sx={{ fontSize: 20, color: isSelected ? GREEN_ACCENT : '#94a3b8' }} />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: isSelected ? '#1e293b' : '#475569' }}>
                                                {cb.chatbot_name}
                                            </Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>
                                                {cb.chatbot_type === 'business' ? 'Business' : 'Personal'}{cb.is_default ? ' \u2022 Default' : ''}
                                            </Typography>
                                        </Box>
                                        {isSelected && <CheckCircleIcon sx={{ fontSize: 20, color: GREEN_ACCENT }} />}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>

                <Divider />

                {/* Live Agent Handoff */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>Live Agent Handoff</Typography>
                <Box sx={{ p: 2, borderRadius: '12px', border: '1px solid', borderColor: 'grey.100', bgcolor: '#fafbfc' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem' }}>Creator Chatbot Live Agent</Typography>
                        </Box>
                        <Switch
                            checked={!!chatbotSettings.is_creator_live_agent_available}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                setChatbotSettings(prev => ({
                                    ...prev,
                                    is_creator_live_agent_available: checked,
                                    ...(!checked ? { live_agent_chatbot_identity: '' } : {}),
                                }));
                            }}
                            sx={switchSx}
                            size="small"
                        />
                    </Box>
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', mb: 1.5 }}>Allow users to connect to a live agent via a business knowledge base.</Typography>
                    {chatbotSettings.is_creator_live_agent_available && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {chatbots.length === 0 ? (
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8', textAlign: 'center', py: 2 }}>No chatbots available</Typography>
                            ) : chatbots.map(cb => {
                                const isSelected = chatbotSettings.live_agent_chatbot_identity === cb.chatbot_identity;
                                return (
                                    <Box
                                        key={cb.chatbot_identity}
                                        onClick={() => setChatbotSettings(prev => ({
                                            ...prev,
                                            live_agent_chatbot_identity: isSelected ? '' : cb.chatbot_identity,
                                        }))}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
                                            borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s ease',
                                            border: '2px solid', bgcolor: isSelected ? `${GREEN_ACCENT}08` : '#fff',
                                            borderColor: isSelected ? GREEN_ACCENT : 'grey.200',
                                            '&:hover': { borderColor: isSelected ? GREEN_ACCENT : '#cbd5e1', bgcolor: isSelected ? `${GREEN_ACCENT}08` : '#f8fafc' },
                                        }}
                                    >
                                        <Box sx={{
                                            width: 36, height: 36, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            bgcolor: isSelected ? `${GREEN_ACCENT}15` : '#f1f5f9',
                                        }}>
                                            <SmartToyIcon sx={{ fontSize: 20, color: isSelected ? GREEN_ACCENT : '#94a3b8' }} />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: isSelected ? '#1e293b' : '#475569' }}>
                                                {cb.chatbot_name}
                                            </Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>
                                                {cb.chatbot_type === 'business' ? 'Business' : 'Personal'}{cb.is_default ? ' \u2022 Default' : ''}
                                            </Typography>
                                        </Box>
                                        {isSelected && <CheckCircleIcon sx={{ fontSize: 20, color: GREEN_ACCENT }} />}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>

                <Divider />

                {/* Language & Voice */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>Audio & Language Settings</Typography>
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontFamily }}>Language</InputLabel>
                            <Select value={selectedLanguage} onChange={(e) => setSelectedLanguage(e.target.value)} label="Language" sx={{ fontFamily, borderRadius: '10px' }}>
                                <MenuItem value="" disabled sx={{ fontFamily }}>Select...</MenuItem>
                                {LanguageSelection.map(l => (
                                    <MenuItem key={l.value} value={l.value} sx={{ fontFamily }}>{l.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {['male', 'female'].map(g => (
                                <Button key={g} variant={selectedGender === g ? 'contained' : 'outlined'} fullWidth
                                    onClick={() => setSelectedGender(g)}
                                    sx={{
                                        fontFamily, textTransform: 'capitalize', fontWeight: 600, borderRadius: '10px', fontSize: '0.8rem',
                                        ...(selectedGender === g ? { bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: '#059669' } } : { borderColor: '#e2e8f0', color: '#64748b' }),
                                    }}>
                                    {g}
                                </Button>
                            ))}
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontFamily }}>Voice</InputLabel>
                            <Select value={selectedVoice} onChange={(e) => setSelectedVoice(e.target.value)} label="Voice" disabled={!selectedLanguage || !selectedGender}
                                sx={{ fontFamily, borderRadius: '10px' }}>
                                <MenuItem value="" disabled sx={{ fontFamily }}>Select...</MenuItem>
                                {availableVoices.map(v => (
                                    <MenuItem key={v.value} value={v.value} sx={{ fontFamily }}>{v.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormControlLabel control={<Checkbox checked={isDefaultLang} onChange={(e) => setIsDefaultLang(e.target.checked)} size="small" sx={{ color: GREEN_ACCENT, '&.Mui-checked': { color: GREEN_ACCENT } }} />}
                                label={<Typography sx={{ fontFamily, fontSize: '0.8rem' }}>Default</Typography>} />
                            <Button variant="contained" onClick={handleAddLanguage} sx={{ ...btnSx, fontSize: '0.8rem', px: 2 }}>Add</Button>
                        </Box>
                    </Grid>
                </Grid>

                {/* Configured Languages */}
                {languages.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', mb: 1.5 }}>Configured Languages</Typography>
                        {languages.map(lang => (
                            <Box key={lang.id} sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                p: 1.5, mb: 1, borderRadius: '10px', bgcolor: '#f8fafc', border: '1px solid', borderColor: 'grey.100',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    {renderPlayButton(lang.website_voice)}
                                    <Box>
                                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>{lang.website_lang}</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                            Voice: {getVoiceName(lang.website_voice)} | Gender: <span style={{ textTransform: 'capitalize' }}>{lang.website_gender}</span>
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {lang.default_language && (
                                        <Chip label="Default" size="small" sx={{ fontFamily, fontSize: '0.7rem', bgcolor: `${GREEN_ACCENT}15`, color: GREEN_ACCENT, fontWeight: 600 }} />
                                    )}
                                    <IconButton size="small" onClick={() => handleDeleteLanguage(lang.id)} sx={{ color: '#ef4444' }}>
                                        <DeleteIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
                {languages.length === 0 && (
                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8' }}>No languages configured yet.</Typography>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                    <Button startIcon={saving.chatbot ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                        variant="contained" onClick={handleSaveChatbot} disabled={saving.chatbot} sx={btnSx}>
                        Save Chatbot Settings
                    </Button>
                </Box>
                    </>
                )}
            </Box>
        );
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: { xs: '1.5rem', md: '2rem' } }}>Settings</Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mt: 0.5 }}>Configure your workspace and integrations</Typography>
                </Box>
                <SettingsIcon sx={{ fontSize: 32, color: '#e2e8f0' }} />
            </Box>

            {/* =================== CHATBOT SETTINGS =================== */}
            <Accordion expanded={expanded === 'chatbot'} onChange={handleAccordionChange('chatbot')} sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <SmartToyIcon sx={{ color: '#8b5cf6' }} />
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>Chatbot Settings</Typography>
                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>Configure chatbot behavior, appearance, knowledge sources, and language</Typography>
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    {renderChatbotSettings()}
                </AccordionDetails>
            </Accordion>

            {/* =================== CENTRAL DRIVE SETTINGS (Admin only) =================== */}
            {isAdmin && (
                <Accordion expanded={expanded === 'drive'} onChange={handleAccordionChange('drive')} sx={accordionSx}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CloudIcon sx={{ color: '#3b82f6' }} />
                            <Box>
                                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>Central Drive Settings</Typography>
                                <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>Connect cloud storage, select a folder, and set your default drive</Typography>
                            </Box>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        {loading.drive ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} sx={{ color: GREEN_ACCENT }} /></Box>
                        ) : (
                            <Box>
                                {/* Setup Progress */}
                                {!isSetupComplete && (
                                    <Alert severity="warning" sx={{ mb: 3, borderRadius: '12px', fontFamily, '& .MuiAlert-message': { fontFamily } }}
                                        icon={<WarningIcon />}>
                                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>Setup Required</Typography>
                                        <Box component="ol" sx={{ pl: 2.5, mt: 0.5, '& li': { fontFamily, fontSize: '0.8rem', mb: 0.25 } }}>
                                            <li style={{ textDecoration: (googleDetails || oneDriveDetails) ? 'line-through' : 'none', opacity: (googleDetails || oneDriveDetails) ? 0.6 : 1 }}>
                                                Connect a cloud storage provider
                                            </li>
                                            <li style={{ textDecoration: (googleFolderConfigured || oneDriveFolderConfigured) ? 'line-through' : 'none', opacity: (googleFolderConfigured || oneDriveFolderConfigured) ? 0.6 : 1 }}>
                                                Select a folder for document storage
                                            </li>
                                            <li style={{ textDecoration: selectedDrive ? 'line-through' : 'none', opacity: selectedDrive ? 0.6 : 1 }}>
                                                Set as default drive
                                            </li>
                                        </Box>
                                    </Alert>
                                )}

                                {/* Drive Cards */}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    {driveOptions.map(drive => (
                                        <Grid item xs={12} sm={6} key={drive.type}>
                                            <Paper sx={{
                                                p: 2.5, borderRadius: '14px',
                                                border: '2px solid',
                                                borderColor: selectedDrive === drive.type && drive.folderConfigured ? GREEN_ACCENT : 'grey.200',
                                                bgcolor: selectedDrive === drive.type && drive.folderConfigured ? `${GREEN_ACCENT}05` : '#fff',
                                            }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem' }}>{drive.name}</Typography>
                                                    {selectedDrive === drive.type && drive.folderConfigured && (
                                                        <Chip label="Default" size="small" sx={{ fontFamily, fontSize: '0.7rem', bgcolor: `${GREEN_ACCENT}15`, color: GREEN_ACCENT, fontWeight: 600 }} />
                                                    )}
                                                </Box>

                                                {drive.details ? (
                                                    <>
                                                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', mb: 1.5 }}>Connected as {drive.details.email}</Typography>

                                                        <Box sx={{ p: 1.5, borderRadius: '8px', bgcolor: '#f8fafc', mb: 2 }}>
                                                            {drive.folderConfigured ? (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <FolderIcon sx={{ fontSize: 18, color: GREEN_ACCENT }} />
                                                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: GREEN_ACCENT, fontWeight: 600 }}>Folder configured</Typography>
                                                                    {drive.details.folder_link && (
                                                                        <Link href={drive.details.folder_link} target="_blank" rel="noopener noreferrer"
                                                                            sx={{ ml: 'auto', fontFamily, fontSize: '0.75rem', color: GREEN_ACCENT, display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                                            View <OpenInNewIcon sx={{ fontSize: 14 }} />
                                                                        </Link>
                                                                    )}
                                                                </Box>
                                                            ) : (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <WarningIcon sx={{ fontSize: 18, color: '#d97706' }} />
                                                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#d97706' }}>Folder not selected</Typography>
                                                                </Box>
                                                            )}
                                                        </Box>

                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                            <Button size="small" variant="outlined" onClick={() => setFolderModalDrive(drive.type)}
                                                                disabled={!!driveActionLoading[drive.type]}
                                                                sx={{ fontFamily, textTransform: 'none', borderRadius: '8px', fontSize: '0.8rem', borderColor: '#e2e8f0', color: '#475569' }}>
                                                                {drive.folderConfigured ? 'Change Folder' : 'Select Folder'}
                                                            </Button>
                                                            <Button size="small" variant="outlined" color="error"
                                                                startIcon={driveActionLoading[drive.type] === 'disconnect' ? <CircularProgress size={14} /> : <LinkOffIcon sx={{ fontSize: 16 }} />}
                                                                onClick={() => setDriveConfirmModal({ type: 'disconnect', drive: drive.type })}
                                                                disabled={!!driveActionLoading[drive.type]}
                                                                sx={{ fontFamily, textTransform: 'none', borderRadius: '8px', fontSize: '0.8rem' }}>
                                                                Disconnect
                                                            </Button>
                                                            {drive.folderConfigured && selectedDrive !== drive.type && (
                                                                <Button size="small" variant="contained"
                                                                    onClick={() => setDriveConfirmModal({ type: 'setDefault', drive: drive.type })}
                                                                    disabled={!!driveActionLoading[drive.type]}
                                                                    sx={{ ...btnSx, fontSize: '0.8rem', ml: 'auto' }}>
                                                                    Set as Default
                                                                </Button>
                                                            )}
                                                        </Box>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8', mb: 2 }}>Not connected</Typography>
                                                        <Button fullWidth variant="contained"
                                                            startIcon={driveActionLoading[drive.type] === 'connect' ? <CircularProgress size={16} color="inherit" /> : null}
                                                            onClick={() => handleConnectDrive(drive.type)}
                                                            disabled={!!driveActionLoading[drive.type]}
                                                            sx={btnSx}>
                                                            {driveActionLoading[drive.type] === 'connect' ? 'Redirecting...' : 'Connect'}
                                                        </Button>
                                                    </>
                                                )}
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>

                                {/* Active Drive Info */}
                                {isSetupComplete && (
                                    <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid', borderColor: 'grey.100' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <CheckCircleIcon sx={{ color: GREEN_ACCENT, fontSize: 20 }} />
                                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem' }}>Active Drive Configuration</Typography>
                                        </Box>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                                            {driveName(selectedDrive)} - {(selectedDrive === 'google_drive' ? googleDetails : oneDriveDetails)?.email}
                                        </Typography>
                                    </Paper>
                                )}
                            </Box>
                        )}
                    </AccordionDetails>
                </Accordion>
            )}

            {/* =================== MODALS =================== */}

            {/* Folder Selection Modal */}
            {folderModalDrive && (
                <FolderSelectionModal
                    open={!!folderModalDrive}
                    onClose={() => setFolderModalDrive(null)}
                    driveType={folderModalDrive}
                    onFolderSelected={(folderId, folderName) => handleFolderSelected(folderModalDrive, folderId, folderName)}
                />
            )}

            {/* Drive Confirmation Modals */}
            <Dialog open={driveConfirmModal.type === 'setDefault'} onClose={() => setDriveConfirmModal({ type: null, drive: null })}
                PaperProps={{ sx: { borderRadius: '16px', maxWidth: 400 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Set Default Drive</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem' }}>
                        Set <strong>{driveName(driveConfirmModal.drive)}</strong> as the default? All documents will use this provider.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDriveConfirmModal({ type: null, drive: null })} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSetDefaultDrive} sx={btnSx}>Confirm</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={driveConfirmModal.type === 'disconnect'} onClose={() => setDriveConfirmModal({ type: null, drive: null })}
                PaperProps={{ sx: { borderRadius: '16px', maxWidth: 400 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Disconnect Drive</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem', mb: 2 }}>
                        Disconnect <strong>{driveName(driveConfirmModal.drive)}</strong>?
                    </Typography>
                    <Alert severity="error" sx={{ borderRadius: '10px', fontFamily, '& .MuiAlert-message': { fontFamily, fontSize: '0.85rem' } }}>
                        <strong>Warning:</strong>
                        <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                            <li>Stored credentials will be removed</li>
                            <li>Folder configuration will be cleared</li>
                            {selectedDrive === driveConfirmModal.drive && <li>This is your default drive - you'll need to reconfigure</li>}
                        </ul>
                    </Alert>
                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', mt: 1.5 }}>
                        Your files in {driveName(driveConfirmModal.drive)} will not be affected.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDriveConfirmModal({ type: null, drive: null })} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDisconnectDrive} sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', fontWeight: 600 }}>Disconnect</Button>
                </DialogActions>
            </Dialog>

            {/* Generate Prompt Drawer */}
            <Drawer
                anchor="right"
                open={isPromptDrawerOpen}
                onClose={() => setIsPromptDrawerOpen(false)}
                PaperProps={{ sx: { width: { xs: '100%', sm: 500 }, borderRadius: '16px 0 0 16px' } }}
            >
                <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>Generate Greeting & System Prompt</Typography>
                        <IconButton onClick={() => setIsPromptDrawerOpen(false)} size="small"><CloseIcon /></IconButton>
                    </Box>
                    {chatbots.length > 0 && getSelectedChatbotId() ? (
                        <>
                            <CreatePrompts
                                ref={createPromptsRef}
                                chatbotId={getSelectedChatbotId()}
                                isSetupMode={false}
                                onFormValidityChange={(valid) => setPromptFormValid(valid)}
                                onComplete={(data) => {
                                    setChatbotSettings(prev => ({
                                        ...prev,
                                        prompt_text: data.prompt_text || prev.prompt_text,
                                        greeting_text: data.greeting_text || prev.greeting_text,
                                    }));
                                    setIsPromptDrawerOpen(false);
                                    showSnackbar('Prompt generated! Review and save your settings.');
                                }}
                                OnError={(msg) => showSnackbar(msg || 'Failed to generate prompt.', 'error')}
                            />
                            <Box sx={{ mt: 3 }}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleGeneratePrompt}
                                    disabled={!promptFormValid || promptGenerating}
                                    startIcon={promptGenerating ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                                    sx={btnSx}
                                >
                                    {promptGenerating ? 'Generating...' : 'Generate & Apply'}
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <Alert severity="info" sx={{ fontFamily, borderRadius: '10px' }}>
                            No chatbots available. Please create a chatbot on the Creator Panel first.
                        </Alert>
                    )}
                </Box>
            </Drawer>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled" sx={{ fontFamily, borderRadius: '10px' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
